import Icon, { LoadingOutlined, ReloadOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import { Space, message } from 'antd';
import React, { useCallback } from 'react';
import { ReactComponent as IconMsg } from 'assets/icons/B-mjsd.svg';
import type { LocalMessage } from 'hooks/business/conversation';
import sysLangToTranslationLang from 'utils/sysLangToTranslationLang';
import { modeTranslate } from 'services/api/overseaTranslate';
import { useTranslate } from '@tolgee/react';
import { ReactComponent as GoogleSvg } from 'views/Conversation/assets/google.svg';
import { ReactComponent as GptSvg } from 'views/Conversation/assets/gpt.svg';
import { ReactComponent as DeepSeekSvg } from 'views/Conversation/assets/deepseek.svg';
import BubbleMessage, { BubbleMessageProps } from './BubbleMessage';
import EnhanceText, { SensitiveWorldEnhanceText } from './EnhanceText';
import { useUpdateMessage } from 'hooks/business/message';

export interface TextMessageProps extends Pick<BubbleMessageProps, 'color' | 'onClick'> {
  className?: string;
  content: string | React.ReactNode;
  translation?: Conversation.Translation;
  layout: 'left' | 'right';
  msg: LocalMessage;
  quote?: React.ReactNode | null;
  conversation?: Conversation.Conversation;
  onTranslate: (msg: LocalMessage, lang?: General.Language) => void;
}

export const TranslatedStatus = React.memo(
  (props: {
    className?: string;
    status?: 'pending' | 'error' | 'success';
    translated?: boolean;
    onTranslate: () => void;
  }) => {
    const { status, translated = false, onTranslate } = props;
    let content = null;

    const { t } = useTranslate();

    switch (status) {
      case 'success': {
        return null;
      }
      case 'pending': {
        content = (
          <div className="translate-status pending">
            <LoadingOutlined />
            {translated ? `${t('ViewsChat.重新翻译中')}...` : t('Entities.翻译中')}
          </div>
        );
        break;
      }
      case 'error': {
        content = (
          <div className="translate-status error" onClick={onTranslate}>
            <ReloadOutlined />
            {t('Entities.翻译失败')}
          </div>
        );
        break;
      }
      default: {
        content = null;
      }
    }
    return (
      <TranslatedFooterDiv className={props.className}>
        <Space size={4}>{content}</Space>
      </TranslatedFooterDiv>
    );
  },
);
const AutoTranslationStatus = React.memo(
  (props: {
    msg: LocalMessage;
    layout: 'left' | 'right';
    translation?: Conversation.Translation;
  }) => {
    const { layout, msg, translation: systemLanguageTranslation } = props;
    const autoTranslation = msg.textContent?.autoTranslation;
    const updateMessage = useUpdateMessage();
    const { t } = useTranslate();
    const resend = useCallback(() => {
      if (
        !msg.resendContext ||
        !autoTranslation ||
        !msg.textContent ||
        msg.localStatus === 'loading'
      ) {
        return;
      }
      const processingMsg: LocalMessage = {
        ...msg.resendContext!.content,
        textContent: {
          ...msg.textContent!,
          text: '',
          autoTranslation: {
            ...autoTranslation,
            status: 'pending',
          },
        },
        failCause: undefined,
        localStatus: 'loading',
      };
      updateMessage(msg.msgId, (prev) => {
        if (!prev) {
          return undefined;
        }
        return {
          ...prev,
          textContent: {
            ...prev.textContent!,
            text: '',
            autoTranslation: {
              ...prev.textContent!.autoTranslation!,
              status: 'pending',
            },
          },
          localStatus: 'loading',
        };
      });
      modeTranslate(msg.textContent.originalText, autoTranslation.targetLanguage, 'GOOGLE')
        .then((res) => {
          const nMsg: LocalMessage = {
            ...processingMsg,
            textContent: {
              ...processingMsg.textContent!,
              text: res,
              autoTranslation: {
                ...processingMsg.textContent!.autoTranslation!,
                status: 'success',
              },
            },
          };
          updateMessage(msg.msgId, nMsg);
          msg.resendContext!.doResend(nMsg);
        })
        .catch(() => {
          updateMessage(msg.msgId, (prev) => {
            if (!prev) {
              return;
            }

            return {
              ...prev,
              textContent: {
                ...prev.textContent!,
                text: '',
                autoTranslation: {
                  ...prev.textContent!.autoTranslation!,
                  status: 'error',
                },
              },
              localStatus: 'error',
            };
          });
          message.error(t('Entities.翻译失败'));
        });
    }, [autoTranslation, msg, t, updateMessage]);

    if (layout === 'left') {
      return null;
    }
    if (
      systemLanguageTranslation?.text ||
      autoTranslation?.text ||
      (!autoTranslation && msg.textContent?.originalText)
    ) {
      return (
        <div className="translate-status success">
          <Icon component={IconMsg} />
          {t('Entities.买家收到')}
        </div>
      );
    }
    if (!autoTranslation) {
      return null;
    }
    const buyerLanguageTranslation =
      msg.textContent?.translationContent?.[
        sysLangToTranslationLang(autoTranslation.targetLanguage)
      ] || autoTranslation;

    return <TranslatedStatus onTranslate={resend} status={buyerLanguageTranslation?.status} />;
  },
);
const Translation = React.memo(
  (props: {
    translation?: Conversation.Translation;
    msg: LocalMessage;
    layout: 'left' | 'right';
  }) => {
    const { layout, msg, translation } = props;
    if (translation) {
      const { mode, text } = translation;

      if (!text) {
        return <Section color="var(--fett-text-tertiary)" />;
      }

      const modeIcon = mode
        ? {
            GOOGLE: <Icon component={GoogleSvg} />,
            CHAT_GPT: <Icon component={GptSvg} />,
            DEEPSEEK: <Icon component={DeepSeekSvg} />,
          }[mode]
        : null;

      return (
        <Section color="var(--fett-text-tertiary)">
          {modeIcon}
          <EnhanceText text={text} platform={msg.channel} />
        </Section>
      );
    }
    if (layout === 'left') {
      return null;
    }
    if (msg.textContent?.originalText) {
      return (
        <Section color="var(--fett-text-tertiary)">
          <EnhanceText text={msg.textContent.originalText} platform={msg.channel} />
        </Section>
      );
    }
    return null;
  },
);

export interface TextMessageBubbleProps
  extends Pick<TextMessageProps, 'color' | 'className' | 'onClick'> {
  extra?: React.ReactNode;
  children?: React.ReactNode;
}

export const TextMessageBubble = React.forwardRef<HTMLDivElement, TextMessageBubbleProps>(
  ({ color, className, children, extra, onClick }, ref) => {
    return (
      <StyledBubbleMessage ref={ref} color={color} className={className} onClick={onClick}>
        <Section color="var(--fett-text-primary)" className="origin-text">
          {children}
        </Section>
        {extra}
      </StyledBubbleMessage>
    );
  },
);

const TextMessage: React.FC<TextMessageProps> = (props) => {
  const { content, translation, layout, className, color, onTranslate, msg, quote } = props;

  const contentNode = (
    <TextMessageBubble
      color={color}
      className={className}
      extra={
        msg.hideTranslate ? null : (
          <>
            <Translation msg={msg} layout={layout} translation={translation} />
            <TranslatedStatus
              onTranslate={() => onTranslate(msg)}
              status={
                translation && !translation.text && translation.status === 'success'
                  ? 'error'
                  : translation?.status
              }
              translated={translation?.translated}
            />
          </>
        )
      }
    >
      {quote}
      {msg.textContent?.autoTranslation &&
      msg.textContent?.autoTranslation?.status !== 'success' ? (
        ''
      ) : msg.failCause?.code === 'LE_CHAT_DETECTED_CENSORED_MSG' && msg.failCause.censorMask ? (
        <SensitiveWorldEnhanceText
          text={content}
          censorMask={msg.failCause.censorMask}
          platform={msg.channel}
        />
      ) : (
        <EnhanceText text={content} platform={msg.channel} />
      )}
      <AutoTranslationStatus translation={translation} msg={msg} layout={layout} />
    </TextMessageBubble>
  );

  return contentNode;
};

export default TextMessage;

const Section = styled.section<{ color: string }>`
  color: ${({ color }) => color};
  word-break: break-all;
  overflow-wrap: break-word;
  white-space: pre-line;
  + section {
    margin-top: 8px;
    padding-top: 8px;
    border-top: 1px solid var(--fett-border-default);
  }
  > .anticon {
    font-size: 16px;
    margin-right: 2px;
  }
`;

const TranslatedFooterDiv = styled.div`
  border-radius: 4px;
  font-size: 12px;
`;

const StyledBubbleMessage = styled(BubbleMessage)`
  position: relative;
  &:hover {
    section.origin-text {
      background-color: var(--fett-bg-light);
    }
  }
  .tip {
    font-size: 12px;
    color: var(--fett-text-tertiary);
    margin: 8px 0;
    svg {
      width: 14px;
      height: 14px;
      margin-right: 4px;
    }
  }
  .translate-status {
    font-size: 12px;
    margin-top: 6px;
    display: flex;
    gap: 4px;
    align-items: center;
    &.error {
      color: var(--fett-app-primary-color);
      cursor: pointer;
    }
    &.success {
      color: var(--fett-text-tertiary);
      svg {
        width: 14px;
        height: 14px;
      }
    }
    &.pending {
      color: var(--fett-app-primary-color);
    }
  }
`;
